<template>
  <div v-if="visible" class="modal-overlay" @click.self="handleClose">
    <div class="modal-container">
      <!-- Header -->
      <div class="modal-header">
        <div class="header-content">
          <div class="header-left">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M20 6H12L10 4H4C2.9 4 2.01 4.9 2.01 6L2 18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V8C22 6.9 21.1 6 20 6ZM20 18H4V6H9.17L11.17 8H20V18ZM18 12H6V10H18V12ZM14 16H6V14H14V16Z" fill="white"/>
            </svg>
            <h2 class="modal-title">{{ formTitle }}</h2>
          </div>
          <button class="close-button" @click="handleClose">
            <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28" fill="none">
              <path fill-rule="evenodd" clip-rule="evenodd" d="M22.1666 7.4785L20.5216 5.8335L13.9999 12.3552L7.47825 5.8335L5.83325 7.4785L12.3549 14.0002L5.83325 20.5218L7.47825 22.1668L13.9999 15.6452L20.5216 22.1668L22.1666 20.5218L15.6449 14.0002L22.1666 7.4785Z" fill="white"/>
              <mask id="mask0_661_38558" style="mask-type:luminance" maskUnits="userSpaceOnUse" x="5" y="5" width="18" height="18">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M22.1666 7.4785L20.5216 5.8335L13.9999 12.3552L7.47825 5.8335L5.83325 7.4785L12.3549 14.0002L5.83325 20.5218L7.47825 22.1668L13.9999 15.6452L20.5216 22.1668L22.1666 20.5218L15.6449 14.0002L22.1666 7.4785Z" fill="white"/>
              </mask>
              <g mask="url(#mask0_661_38558)">
              </g>
            </svg>
          </button>
        </div>
      </div>

      <!-- Content -->
      <div class="modal-content">
        <div class="form-scroll-container">
          <!-- Loading State -->
          <div v-if="loading" class="loading-container">
            <div class="loading-spinner"></div>
            <p class="loading-text">Loading form preview...</p>
          </div>
          
          <!-- Form Content -->
          <div v-else class="form-preview-wrapper">
            <template v-for="element in formElements" :key="element.id">
              <component
                :is="getFormComponent(element.type)"
                :data="element.data"
                v-model="formData[element.id]"
              />
            </template>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="modal-footer">
        <div class="footer-content">
          <!-- Original:
          <button v-if="hasCancelButton" class="cancel-button" @click="handleClose">
            Cancel
          </button>
          -->
          <ResponsiveButton
            v-if="hasCancelButton"
            display_name="Cancel"
            button_type="outlined"
            built_in_theme="error"
            size="medium"
            @click-button="handleClose"
          />
          <!-- Original:
          <button v-if="hasSubmitButton" class="submit-button" @click="handleSubmit">
            Submit Form
          </button>
          -->
          <ResponsiveButton
            v-if="hasSubmitButton"
            display_name="Submit Form"
            button_type="filled"
            built_in_theme="dark"
            size="medium"
            @click-button="handleSubmit"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { formComponentMap } from '@/views/my-workbench-new/document-template/create-document/components/formComponents/index.js';

export default {
  name: 'FormPreviewModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    formTitle: {
      type: String,
      default: ''
    },
    formElements: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    hasSubmitButton: {
      type: Boolean,
      default: true
    },
    hasCancelButton: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      formData: {}
    };
  },
  emits: ['close', 'submit'],
  watch: {
    visible(newVal) {
      if (newVal) {
        // Initialize form data when modal opens
        this.initializeFormData();
        // Disable body scroll
        document.body.style.overflow = 'hidden';
      } else {
        // Enable body scroll
        document.body.style.overflow = '';
      }
    }
  },
  beforeUnmount() {
    // Cleanup: ensure body scroll is re-enabled
    document.body.style.overflow = '';
  },
  methods: {
    getFormComponent(type) {
      return formComponentMap[type];
    },
    initializeFormData() {
      const output = {};
      this.formElements.forEach(element => {
        // Set default value based on element type
        switch (element.type) {
          case 'multi-selection':
            output[element.id] = [];
            break;
          case 'checkbox':
            output[element.id] = false;
            break;
          case 'yes-no':
            output[element.id] = null;
            break;
          case 'rating':
            output[element.id] = 0;
            break;
          case 'numeric':
            output[element.id] = null;
            break;
          case 'date':
          case 'datetime':
          case 'time':
            output[element.id] = null;
            break;
          // Display-only elements don't need v-model
          case 'section-header':
          case 'description':
          case 'note-help':
          case 'image':
          case 'clinic-branding':
            // No data binding needed
            break;
          // All other text-based inputs (short-response, long-response, email, phone, etc.)
          default:
            output[element.id] = '';
        }
      });
      this.formData = output;
    },
    handleClose() {
      this.$emit('close');
    },
    handleSubmit() {
      console.log('Form submitted with data:', this.formData);
      this.$emit('submit', this.formData);
    }
  }
};
</script>

<style scoped>
.modal-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  z-index: 9999;
  padding-top: 80px;
  padding-bottom: 20px;
  overflow-y: auto;
}

.modal-container {
  width: 70%;
  max-height: calc(100vh - 100px);
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  margin: 0 auto;
}

/* Responsive adjustments for smaller screens */
@media (max-width: 1580px) {
  .modal-overlay {
    padding-left: 20px;
    padding-right: 20px;
  }
  
  .modal-container {
    width: 80%;
  }
}

@media (max-width: 768px) {
  .modal-overlay {
    padding-top: 60px;
  }
  
  .modal-container {
    width: 80%;
    max-height: calc(100vh - 80px);
  }
}

/* Header */
.modal-header {
  height: 60px;
  border-radius: 10px 10px 0 0;
  border-bottom: 1px solid #E0E0E0;
  flex-shrink: 0;
  background-color: #004879;
}

.header-content {
  padding: 16px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 100%;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.modal-title {
  font-family: 'Inter', sans-serif;
  font-size: 18px;
  font-weight: 600;
  line-height: 24px;
  color: #FFF;
  margin: 0;
}

.close-button {
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 4px;
  transition: background 0.2s;
}

.close-button:hover {
  background: rgba(0, 0, 0, 0.05);
}

/* Content */
.modal-content {
  height: 100%;
  overflow: auto;
  min-height: 0;
  background-color: #fff;
}

.form-scroll-container {
  height: fit-content;
  padding: 24px 32px;
}

.form-preview-wrapper {
  max-width: 996px;
  margin: 0 auto;
  background: #fff;
  border-radius: 8px;
  padding-bottom: 24px;
}

/* Footer */
.modal-footer {
  height: 88px;
  border-top: 1px solid #E0E0E0;
  flex-shrink: 0;
}

.footer-content {
  padding: 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 100%;
  background-color: #fff;
  border-radius: 0 0 10px 10px;
}

.cancel-button {
  padding: 10px 24px;
  background: transparent;
  border: 1px solid #C9C9C9;
  border-radius: 4px;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #374957;
  cursor: pointer;
  transition: all 0.2s;
}

.cancel-button:hover {
  background: #F5F5F5;
  border-color: #999;
}

.submit-button {
  padding: 10px 24px;
  background: #004879;
  border: none;
  border-radius: 4px;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #fff;
  cursor: pointer;
  transition: background 0.2s;
}

.submit-button:hover {
  background: #1E4670;
}

/* Scrollbar styling */
.form-scroll-container::-webkit-scrollbar {
  width: 8px;
}

.form-scroll-container::-webkit-scrollbar-track {
  background: #F5F5F5;
  border-radius: 4px;
}

/* Loading State */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  gap: 16px;
}

.loading-spinner {
  width: 48px;
  height: 48px;
  border: 4px solid #E0E0E0;
  border-top-color: #004879;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.loading-text {
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 500;
  color: #374957;
  margin: 0;
}

.form-scroll-container::-webkit-scrollbar-thumb {
  background: #C9C9C9;
  border-radius: 4px;
}

.form-scroll-container::-webkit-scrollbar-thumb:hover {
  background: #999;
}
</style>
